import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Europcar Türkiye — Programatik Ağırlıklı Medya Planı (Eylül–Kasım 2026)
 *
 * Planın omurgası PLACEMENT HEDEFLEMESİ. Bu yüzden sayfadaki "mevcut durum"
 * rakamlarının hiçbiri sektör ortalaması değil, Europcar'ın kendi hesabının
 * ambardaki gerçek verisi:
 *
 *   brand_europcar_13b58e06_8266_4a5b_8113_2de43aa686e1
 *     · google_ads_gdn_placement_performance_raw   (1 Eki – 5 Kas 2025, 47.060 satır)
 *     · google_ads_youtube_placement_raw           (14–31 Eki 2025)
 *     · google_ads_campaign_performance_raw        (1 Eki 2025 – 19 Ağu 2026)
 *     · meta_placement_performance_raw             (son 90 gün)
 *     · ga_geo_performance_raw / ga_device_performance_raw / ga_traffic_source_raw
 *     · search_console_totals_daily_raw / search_console_page_daily_raw
 *
 * 🔑 Dönüşüm iddiası KAMPANYA SEVİYESİNDEN doğrulandı. Placement raporunda
 * conversions sütunu her satırda 0 döner; bu tek başına "placement kötü" demek
 * değildir (rapor dönüşüm taşımıyor olabilir). Bu yüzden ayrıca kampanya
 * tablosuna bakıldı: iki GDN remarketing kampanyası 2.006.117 gösterim ve
 * 28.276,64 ₺ karşılığında GERÇEKTEN 0 dönüşüm üretmiş.
 *
 * ⚠️ CPM'lerin bir kısmı ölçüm, bir kısmı tahmin. Her satırda etiketli —
 * tahmin olanlar teklif geldikçe güncellenmeli, plan onlara göre yeniden hesaplar.
 * ⚠️ Dönem (Eylül–Kasım 2026) benim varsayımım, onaya açık.
 *
 * Diğer Europcar sayfalarıyla aynı: yalnız AÇIK TEMA, `dark:` varyantı bilerek yok.
 */

const YESIL = '#009900';
const KOYU = '#0f3d0f';

export default function EuropcarProgramatik2026() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [senaryo, setSenaryo] = useState('B');

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtDec = (n, d = 2) =>
    Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

  // ══ Bütçe senaryoları ═══════════════════════════════════════════════════
  // Kullanıcının verdiği bant: aylık 250–500K ₺. Üçü de gerçek plan, B önerilen.
  // 250K'da CTV ve pDOOH bilerek SIFIR — o bütçede ikisine de anlamlı frekans
  // düşmez, parayı bölmek yerine ölçülebilir iki kaleme yığmak daha doğru.
  const SENARYOLAR = {
    A: { ad: 'Odaklı', aylik: 250000 },
    B: { ad: 'Önerilen', aylik: 400000 },
    C: { ad: 'Geniş', aylik: 500000 },
  };
  const AY_SAYISI = 3;

  const KALEMLER = [
    {
      kod: 1,
      kalem: 'PMP — Seyahat & Havalimanı bağlamı',
      tur: 'programatik',
      aciklama: 'Havalimanı, havayolu, OTA ve tatil envanteri. Whitelist + deal ID.',
      butce: { A: 80000, B: 90000, C: 110000 },
      birim: 'cpm',
      deger: 55,
      kaynak: 'tahmin',
      kaynakNot: 'Premium seyahat envanteri; teklif alınacak',
    },
    {
      kod: 2,
      kalem: 'PMP — Premium haber & ekonomi',
      tur: 'programatik',
      aciklama: 'Sayfa düzeyinde bağlam filtresi zorunlu. Ana sayfa/kategori, makale değil.',
      butce: { A: 45000, B: 60000, C: 70000 },
      birim: 'cpm',
      deger: 32,
      kaynak: 'ölçüm',
      kaynakNot: 'Kendi GDN verimiz: 16,54–43,66 ₺ bandı',
    },
    {
      kod: 3,
      kalem: 'CTV / BVOD',
      tur: 'programatik',
      aciklama: 'Bağlantılı TV. Ses açık, tam ekran, atlanamaz envanter.',
      butce: { A: 0, B: 55000, C: 75000 },
      birim: 'cpm',
      deger: 120,
      kaynak: 'tahmin',
      kaynakNot: 'TR CTV bandı; erişilebilirlik doğrulanmadı',
    },
    {
      kod: 4,
      kalem: 'YouTube — kanal whitelist',
      tur: 'programatik',
      aciklama: 'Seyahat/otomobil/rota kanalları. Kategori değil, kanal listesi.',
      butce: { A: 40000, B: 45000, C: 55000 },
      birim: 'cpm',
      deger: 26,
      kaynak: 'tahmin',
      kaynakNot: 'Ölçülen RON video CPM 10,58 ₺; whitelist daha pahalıdır',
    },
    {
      kod: 5,
      kalem: 'Programatik DOOH',
      tur: 'programatik',
      aciklama: 'Havalimanı iç mekân, otoyol, AVM, akaryakıt istasyonu ekranları.',
      butce: { A: 0, B: 40000, C: 60000 },
      birim: 'cpm',
      deger: 55,
      kaynak: 'tahmin',
      kaynakNot: 'pDOOH bandı; envanter sahibi teyidi şart',
    },
    {
      kod: 6,
      kalem: 'Yerel & Harita katmanı (Google Ads)',
      tur: 'yerel',
      aciklama: 'Hesabın en verimli kalemi. Programatik değil ama dokunulmamalı.',
      butce: { A: 45000, B: 50000, C: 55000 },
      birim: 'cpc',
      deger: 2.6,
      kaynak: 'ölçüm',
      kaynakNot: 'Harita kampanyaları gerçekleşen CPC 1,47–3,73 ₺',
    },
    {
      kod: 7,
      kalem: 'Dinamik retargeting (whitelist\'li)',
      tur: 'programatik',
      aciklama: 'Aynı kara liste burada da geçerli. Sıklık sınırlı, lokasyon kreatifli.',
      butce: { A: 30000, B: 40000, C: 50000 },
      birim: 'cpm',
      deger: 20,
      kaynak: 'ölçüm',
      kaynakNot: 'Gerçekleşen GDN CPM 16,54 ₺',
    },
    {
      kod: 8,
      kalem: 'Ölçüm, doğrulama & kreatif adaptasyon',
      tur: 'ölçüm',
      aciklama: 'Görünürlük/marka güvenliği doğrulaması + format üretimi.',
      butce: { A: 10000, B: 20000, C: 25000 },
      birim: '-',
      deger: 0,
      kaynak: '-',
      kaynakNot: 'Medya dışı kalem',
    },
  ];

  const aylik = SENARYOLAR[senaryo].aylik;
  const satirlar = KALEMLER.map((k) => {
    const b = k.butce[senaryo];
    const gosterim = k.birim === 'cpm' && b > 0 ? (b / k.deger) * 1000 : 0;
    const tik = k.birim === 'cpc' && b > 0 ? b / k.deger : 0;
    return { ...k, b, gosterim, tik, pay: b / aylik };
  });

  const toplamGosterim = satirlar.reduce((s, r) => s + r.gosterim, 0);
  const dooh = satirlar.find((r) => r.kod === 5);
  const ekranGosterim = toplamGosterim - dooh.gosterim; // kişi bazlı erişim yalnız ekran içi
  const FREKANS = 4; // ⚠️ varsayım — 30 günlük akışta hedeflenen ortalama frekans
  const erisim = ekranGosterim / FREKANS;
  const programatikTutar = satirlar
    .filter((r) => r.tur === 'programatik')
    .reduce((s, r) => s + r.b, 0);
  const programatikPay = programatikTutar / aylik;
  const yerelTutar = satirlar.filter((r) => r.tur === 'yerel').reduce((s, r) => s + r.b, 0);
  const olcumTutar = satirlar.filter((r) => r.tur === 'ölçüm').reduce((s, r) => s + r.b, 0);
  const donemToplam = aylik * AY_SAYISI;

  // ══ ÖLÇÜM — mevcut durum ════════════════════════════════════════════════
  // Hepsi ambardan, 1 Eki – 5 Kas 2025 GDN penceresi.
  const GDN = {
    gosterim: 1778058,
    tik: 19597,
    maliyet: 29403.22,
    cpm: 16.54,
    ctr: 1.1,
    placementSayisi: 23313,
    baslangic: '1 Eki 2025',
    bitis: '5 Kas 2025',
  };
  const GDN_KAMPANYA = { gosterim: 2006117, maliyet: 28276.64, donusum: 0 };
  const YT_KAMPANYA = { gosterim: 472725, tik: 20, maliyet: 5000.62, donusum: 0 };
  const DERIN = { adet: 23233, gosterim: 1206454, tik: 14724, maliyet: 21291.56 };
  const ANASAYFA = { adet: 52, gosterim: 216546, tik: 1496, maliyet: 2517.09 };
  const SOZCU_IOS = { gosterim: 225476, tik: 2625, maliyet: 4136.78, cpm: 18.35 };

  // Alan bazında gerçekleşen — planın whitelist/kara liste kararlarının dayanağı
  const ALANLAR = [
    { alan: 'www.cumhuriyet.com.tr', gosterim: 343162, tik: 3642, ctr: 1.06, maliyet: 5447.87, cpm: 15.88 },
    { alan: 'www.sozcu.com.tr', gosterim: 147168, tik: 2611, ctr: 1.77, maliyet: 4132.1, cpm: 28.08 },
    { alan: 'www.cnnturk.com', gosterim: 80125, tik: 2994, ctr: 3.74, maliyet: 3498.39, cpm: 43.66 },
    { alan: 'www.fanatik.com.tr', gosterim: 103339, tik: 1647, ctr: 1.59, maliyet: 2130.42, cpm: 20.62 },
    { alan: 'www.tele1.com.tr', gosterim: 139192, tik: 973, ctr: 0.7, maliyet: 1413.76, cpm: 10.16 },
    { alan: 'www.hurriyet.com.tr', gosterim: 162076, tik: 827, ctr: 0.51, maliyet: 1224.95, cpm: 7.56 },
    { alan: 'www.haberler.com', gosterim: 113445, tik: 430, ctr: 0.38, maliyet: 848.29, cpm: 7.48 },
    { alan: 'www.karar.com', gosterim: 63813, tik: 491, ctr: 0.77, maliyet: 725.81, cpm: 11.37 },
    { alan: 'www.mynet.com', gosterim: 48854, tik: 351, ctr: 0.72, maliyet: 577.47, cpm: 11.82 },
    { alan: 'www.posta.com.tr', gosterim: 41819, tik: 339, ctr: 0.81, maliyet: 543.46, cpm: 13.0 },
    { alan: 'www.sabah.com.tr', gosterim: 15301, tik: 322, ctr: 2.1, maliyet: 418.43, cpm: 27.35 },
    { alan: 'onedio.com', gosterim: 9434, tik: 161, ctr: 1.71, maliyet: 297.35, cpm: 31.52 },
    { alan: 'www.dunya.com', gosterim: 14663, tik: 165, ctr: 1.13, maliyet: 207.21, cpm: 14.13 },
    { alan: 'www.mackolik.com', gosterim: 27002, tik: 4, ctr: 0.01, maliyet: 218.24, cpm: 8.08 },
  ];

  // Meta placement — son 90 gün, canlı hesap
  const META = [
    { yer: 'Facebook (mobil uygulama)', gosterim: 1881275, tik: 81573, ctr: 4.34, harcama: 58983.93, cpm: 31.35, karar: 'kalsın' },
    { yer: 'Instagram (mobil uygulama)', gosterim: 746263, tik: 10735, ctr: 1.44, harcama: 58889.14, cpm: 78.91, karar: 'gözden geçir' },
    { yer: 'Audience Network', gosterim: 417217, tik: 67809, ctr: 16.25, harcama: 14070.56, cpm: 33.72, karar: 'kapat' },
    { yer: 'Facebook (masaüstü)', gosterim: 36215, tik: 208, ctr: 0.57, harcama: 727.49, cpm: 20.09, karar: 'kalsın' },
    { yer: 'Threads', gosterim: 43766, tik: 138, ctr: 0.32, harcama: 349.15, cpm: 7.98, karar: 'test' },
  ];

  // Google Ads son 90 gün — programatiğin aşması gereken eşik
  const GADS = [
    { kampanya: 'MVM - Performance Max', maliyet: 461803.93, donusum: 1783, cpa: 259, tip: 'PMax' },
    { kampanya: 'MVM / Search - Brand - Always-ON', maliyet: 359947.76, donusum: 1395, cpa: 258, tip: 'Marka' },
    { kampanya: 'MVM // Kocaeli Harita', maliyet: 2415.5, donusum: 259, cpa: 9, tip: 'Harita' },
    { kampanya: 'MVM // Edremit Harita', maliyet: 7134.13, donusum: 261, cpa: 27, tip: 'Harita' },
    { kampanya: 'MVM / Search - Kocaeli - Always-ON', maliyet: 11505.66, donusum: 26, cpa: 443, tip: 'Şehir' },
    { kampanya: 'MVM / Search - Antalya - Always-ON', maliyet: 22958.61, donusum: 33, cpa: 696, tip: 'Şehir' },
    { kampanya: 'MVM / Search - Konya - Always-ON', maliyet: 19075.15, donusum: 13, cpa: 1467, tip: 'Şehir' },
    { kampanya: 'MVM / Search - Çanakkale - Always-ON', maliyet: 14412.74, donusum: 8, cpa: 1802, tip: 'Şehir' },
    { kampanya: 'MVM / Search - Kars - Always-ON', maliyet: 10057, donusum: 1, cpa: 10057, tip: 'Şehir' },
  ];

  // ══ Whitelist ═══════════════════════════════════════════════════════════
  // durum: 'kanit' = kendi verimizde kanıtlı · 'teklif' = teklif alınacak
  //        'dogrulanmadi' = programatik erişilebilirliği teyit edilmedi
  const WHITELIST = [
    {
      grup: 'Havalimanı & havayolu',
      renk: 'emerald',
      gerekce:
        'Bu siteler bize ZATEN trafik gönderiyor — üstelik para vermeden. Referans verisi 180 gün.',
      envanter: [
        { ad: 'antalya-airport.aero', kanit: '871 oturum / 180 gün (referral)', durum: 'kanit' },
        { ad: 'cukurovaairport.aero', kanit: '573 oturum / 180 gün (referral)', durum: 'kanit' },
        { ad: 'istairport.com (İGA)', kanit: '343 oturum / 180 gün (referral)', durum: 'kanit' },
        { ad: 'turkishairlines.com', kanit: '342 oturum / 180 gün (referral)', durum: 'kanit' },
        { ad: 'flypgs.com + web.flypgs.com', kanit: '595 oturum / 180 gün (referral)', durum: 'kanit' },
        { ad: 'sabihagokcen.aero (ISG)', kanit: 'Ofisimiz var; referans görünmüyor', durum: 'teklif' },
        { ad: 'esenbogaairport / adnanmenderesairport (TAV)', kanit: 'Ofis ağı örtüşüyor', durum: 'teklif' },
        { ad: 'dalamanairport / bodrum-milas / trabzon', kanit: 'Sezonluk giriş kapıları', durum: 'teklif' },
      ],
    },
    {
      grup: 'Seyahat / OTA & ulaşım',
      renk: 'sky',
      gerekce:
        'Niyet, uçuş veya otobüs bileti alınırken doğuyor. Araç kiralama kararı o anın 1–2 adım sonrası.',
      envanter: [
        { ad: 'enuygun.com', kanit: 'Uçuş + otobüs + araç kiralama karşılaştırma', durum: 'teklif' },
        { ad: 'obilet.com', kanit: 'Şehirlerarası ulaşım niyeti', durum: 'teklif' },
        { ad: 'turna.com / biletall.com', kanit: 'Bilet satın alma anı', durum: 'teklif' },
        { ad: 'tatilsepeti / tatilbudur / jollytur / setur / etstur', kanit: 'Tatil planlama hunisi', durum: 'teklif' },
        { ad: 'trivago.com.tr / tripadvisor.com.tr', kanit: 'Destinasyon araştırma', durum: 'teklif' },
      ],
    },
    {
      grup: 'Premium haber & ekonomi (sayfa düzeyi filtreli)',
      renk: 'indigo',
      gerekce:
        'Kendi ölçümümüzde en iyi CTR bu grupta çıktı — ama SADECE ana sayfa/kategori düzeyinde alınırsa.',
      envanter: [
        { ad: 'cnnturk.com', kanit: 'CTR %3,74 · CPM 43,66 ₺ — ölçülen en iyi CTR', durum: 'kanit' },
        { ad: 'sozcu.com.tr (web)', kanit: 'CTR %1,77 · CPM 28,08 ₺', durum: 'kanit' },
        { ad: 'sabah.com.tr + m.sabah.com.tr', kanit: 'CTR %2,10 / %2,20', durum: 'kanit' },
        { ad: 'onedio.com', kanit: 'CTR %1,71 · CPM 31,52 ₺', durum: 'kanit' },
        { ad: 'dunya.com / bloomberght.com / ekonomim.com', kanit: 'Kurumsal & filo kitlesi', durum: 'teklif' },
        { ad: 'ntv.com.tr / haberturk.com', kanit: 'Erişim tamamlayıcı', durum: 'teklif' },
      ],
    },
    {
      grup: 'Bağlam tetikleyicileri',
      renk: 'amber',
      gerekce: 'Envanterden değil, ANDAN satın alma. Hava, yol, tatil takvimi.',
      envanter: [
        { ad: 'Hava durumu envanteri (mgm.gov.tr, AccuWeather TR)', kanit: 'Kar/yağış tetikleyicisi → 4×4 ve kış lastiği mesajı', durum: 'teklif' },
        { ad: 'Navigasyon & yol (Yandex Navi, Waze)', kanit: 'Waze ayrı platform, DV360 dışı', durum: 'dogrulanmadi' },
        { ad: 'Programatik audio (Spotify, podcast ağları)', kanit: 'Araç içi/yolculuk dinleme anı', durum: 'teklif' },
      ],
    },
    {
      grup: 'CTV / BVOD',
      renk: 'rose',
      gerekce: 'Bilinirlik hedefinin taşıyıcısı. Atlanamaz, ses açık, ev ekranı.',
      envanter: [
        { ad: 'Exxen · blu TV · TOD · Tabii', kanit: 'Yerli BVOD envanteri', durum: 'dogrulanmadi' },
        { ad: 'Samsung TV Plus · LG Channels', kanit: 'FAST kanallar, TV üreticisi envanteri', durum: 'dogrulanmadi' },
        { ad: 'YouTube CTV (DV360 üzerinden)', kanit: 'Aynı DSP\'den alınabilir', durum: 'teklif' },
      ],
    },
    {
      grup: 'Programatik DOOH',
      renk: 'violet',
      gerekce:
        'Lokasyon hedefinin fiziksel karşılığı: insan zaten oradayken, ofisin 200 m yakınında.',
      envanter: [
        { ad: 'Havalimanı iç mekân ekranları (bagaj/geliş)', kanit: 'Kiralama kararının verildiği fiziksel an', durum: 'dogrulanmadi' },
        { ad: 'Otoyol & şehir giriş dijital panolar', kanit: 'Transit kitle', durum: 'teklif' },
        { ad: 'AVM & akaryakıt istasyonu ekranları', kanit: 'Şehir içi frekans', durum: 'teklif' },
      ],
    },
  ];

  // ══ Kara liste — hepsi kendi verimizden ═════════════════════════════════
  const KARALISTE = [
    {
      baslik: 'Derin makale URL\'leri (asıl sorun bu)',
      kanit: `${fmtInt(DERIN.adet)} ayrı makale adresi · ${fmtCur(DERIN.maliyet)} · toplam GDN bütçesinin %${fmtDec((DERIN.maliyet / GDN.maliyet) * 100, 1)}'i`,
      neden:
        'Yayıncıyla ilişki kurulmamış, bütçe sayfa sayfa savrulmuş. 23 binden fazla adrese bölünen bir bütçede hiçbir adres öğrenmeye yetecek veri toplayamaz.',
    },
    {
      baslik: 'Magazin / asayiş / ölüm haberleri',
      kanit:
        'Örnek: Hürriyet canlı blog sayfası 19.601 gösterim → 3 tık (CTR %0,02). Güllü ve cinayet haberleri ilk 40 placement\'ın 12\'si.',
      neden:
        'Hem performans hem marka güvenliği. Bir araç kiralama markası cinayet haberinin yanında görünmemeli — bu bir optimizasyon meselesi değil, konumlandırma meselesi.',
    },
    {
      baslik: 'Canlı skor / arşiv / canlı yayın sayfaları',
      kanit: 'mackolik.com/haberler/arsiv: 18.911 gösterim, 4 tık, CTR %0,01, 147,57 ₺',
      neden: 'Sekmede açık kalan, bakılmayan sayfa. Gösterim sayılır, insan görmez.',
    },
    {
      baslik: 'Tek uygulamanın bütçeyi yutması',
      kanit: `Sözcü iOS uygulaması: ${fmtCur(SOZCU_IOS.maliyet)} = GDN bütçesinin %${fmtDec((SOZCU_IOS.maliyet / GDN.maliyet) * 100, 1)}'i, tek başına`,
      neden:
        'Uygulama içi banner, haber okurken kazara dokunulan alan. Placement başına harcama tavanı olmadığı için tek envanter bütçeyi domine etmiş.',
    },
    {
      baslik: 'Oyun uygulamaları',
      kanit: 'Dream League Soccer, 8 Ball Pool, Candy Crush, OSM, Efsane Başkan, Soccer Manager…',
      neden:
        'Araç kiralama niyetiyle hiçbir ilgisi yok. Tutar küçük ama sinyal net: envanter seçilmiyor, otomatik dağıtılıyor.',
    },
    {
      baslik: 'Meta Audience Network',
      kanit: `CTR %16,25 · ${fmtCur(14070.56)} · GA4'te "an" kaynağından 24.717 oturum`,
      neden:
        '%16 CTR bir başarı değil, kaza tıklaması imzasıdır. Feed yerleşimlerinin tamamından fazla oturum getirmesi de bunu doğruluyor.',
    },
    {
      baslik: 'MFA (reklam için üretilmiş) siteler & otomatik çeviri içerik',
      kanit: 'Ön-teklif (pre-bid) filtresiyle engellenir',
      neden: 'Standart hijyen. DV360 + doğrulama iş ortağı katmanında kapatılır.',
    },
  ];

  // ══ Coğrafya ════════════════════════════════════════════════════════════
  const SEHIRLER = [
    { sehir: 'İstanbul', oturum: 113001, agirlik: 'Yüksek', not: 'İki havalimanı + şehir içi; en derin envanter' },
    { sehir: 'Ankara', oturum: 55554, agirlik: 'Yüksek', not: 'Kurumsal/kamu yoğun, hafta içi ağırlık' },
    { sehir: 'İzmir', oturum: 33187, agirlik: 'Yüksek', not: 'ADB + Çeşme sezonluk' },
    { sehir: 'Bursa', oturum: 8092, agirlik: 'Orta', not: 'Sanayi + hafta sonu kaçamağı' },
    { sehir: 'Konya', oturum: 7507, agirlik: 'Orta', not: 'Aramada CPA 1.467 ₺ — programatik daha ucuz olmalı' },
    { sehir: 'Antalya', oturum: 7210, agirlik: 'Yüksek', not: 'Havalimanı referansı ölçülen en güçlüsü' },
    { sehir: 'Kayseri', oturum: 4348, agirlik: 'Orta', not: 'Kapadokya giriş kapısı' },
    { sehir: 'Diyarbakır', oturum: 4118, agirlik: 'Orta', not: 'Aramada rekabet düşük' },
    { sehir: 'Adana', oturum: 3912, agirlik: 'Orta', not: 'Çukurova havalimanı referansı canlı' },
    { sehir: 'Mersin', oturum: 3202, agirlik: 'Orta', not: 'Organik gösterimi yüksek, CTR düşük' },
  ];

  const YURTDISI = [
    { ulke: 'Almanya', oturum: 30549, not: 'Türkiye\'ye gelen en büyük yabancı kaynak — ayrı kurgu hak ediyor' },
    { ulke: 'Hollanda', oturum: 8139, not: 'Yaz + bayram dönemi yoğunluğu' },
    { ulke: 'Fransa', oturum: 7877, not: '' },
    { ulke: 'İngiltere', oturum: 5185, not: '' },
    { ulke: 'İsviçre', oturum: 5178, not: 'Kişi başı harcama yüksek segment' },
    { ulke: 'Avusturya', oturum: 3584, not: '' },
    { ulke: 'Belçika', oturum: 2460, not: '' },
  ];

  // Sezon — Search Console tıkları (site geneli), aylık
  const SEZON = [
    { ay: '2025-09', tik: 38853 },
    { ay: '2025-10', tik: 31264 },
    { ay: '2025-11', tik: 44160 },
    { ay: '2025-12', tik: 32772 },
    { ay: '2026-01', tik: 26120 },
    { ay: '2026-02', tik: 22659 },
    { ay: '2026-03', tik: 37738 },
    { ay: '2026-04', tik: 34908 },
    { ay: '2026-05', tik: 52227 },
    { ay: '2026-06', tik: 64801 },
    { ay: '2026-07', tik: 68930 },
  ];
  const sezonMax = Math.max(...SEZON.map((s) => s.tik));

  // ══ PDF ═════════════════════════════════════════════════════════════════
  const exportPdf = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const root = slideRef.current;
      if (!root) return;
      const headerEl = root.querySelector('[data-pdf-header]');
      const sectionEls = Array.from(root.querySelectorAll('[data-pdf-section]'));

      const shoot = (el) =>
        html2canvas(el, {
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true,
          width: el.offsetWidth,
          height: el.scrollHeight + 12,
          windowWidth: document.documentElement.clientWidth,
        });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const M = 8;
      const GAP = 6;
      const CW = pw - M * 2;
      const BOTTOM = ph - M - 6;

      const headerCanvas = await shoot(headerEl);
      const headerImg = headerCanvas.toDataURL('image/jpeg', 0.95);
      const headerH = (headerCanvas.height * CW) / headerCanvas.width;
      const TOP_Y = M + headerH + GAP;
      const PAGE_H = BOTTOM - TOP_Y;

      let pages = 0;
      let y = 0;
      const startPage = () => {
        if (pages > 0) pdf.addPage();
        pages += 1;
        pdf.addImage(headerImg, 'JPEG', M, M, CW, headerH);
        y = TOP_Y;
      };

      const slice = (canvas, srcY, srcH) => {
        const c = document.createElement('canvas');
        c.width = canvas.width;
        c.height = srcH;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        return c.toDataURL('image/jpeg', 0.95);
      };

      startPage();
      for (const el of sectionEls) {
        const canvas = await shoot(el);
        const pxPerMm = canvas.width / CW;
        let srcY = 0;
        let remain = canvas.height / pxPerMm;

        while (remain > 0.5) {
          const avail = BOTTOM - y;
          if (remain <= avail) {
            const srcH = Math.round(remain * pxPerMm);
            pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, remain);
            y += remain + GAP;
            remain = 0;
            break;
          }
          if (remain <= PAGE_H) {
            startPage();
            continue;
          }
          const srcH = Math.round(avail * pxPerMm);
          pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, avail);
          srcY += srcH;
          remain -= avail;
          startPage();
        }
      }

      for (let i = 1; i <= pages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150);
        pdf.text(
          `Europcar Türkiye — Programatik Medya Planı · Glowytics · ${i}/${pages}`,
          pw / 2,
          ph - M + 2,
          { align: 'center' }
        );
      }

      pdf.save('Europcar_Programatik_Medya_Plani.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  // ══ Küçük bileşenler ════════════════════════════════════════════════════
  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      amber: 'border-amber-100 bg-amber-50 text-amber-800',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1 leading-tight">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-1 leading-snug">{sub}</p>}
      </div>
    );
  };

  const Section = ({ title, kicker, children, tone = 'white' }) => (
    <div
      data-pdf-section
      className={`rounded-xl border p-5 ${tone === 'white' ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}
    >
      {kicker && (
        <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: YESIL }}>
          {kicker}
        </p>
      )}
      <h2 className="text-base font-bold text-gray-900 mt-0.5 mb-3">{title}</h2>
      {children}
    </div>
  );

  const DurumRozet = ({ durum }) => {
    const map = {
      kanit: { t: 'veride kanıtlı', c: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      teklif: { t: 'teklif alınacak', c: 'bg-amber-50 text-amber-700 border-amber-200' },
      dogrulanmadi: { t: 'doğrulanmadı', c: 'bg-gray-100 text-gray-600 border-gray-200' },
    };
    const m = map[durum] || map.dogrulanmadi;
    return (
      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${m.c}`}>
        {m.t}
      </span>
    );
  };

  const KaynakRozet = ({ kaynak }) =>
    kaynak === 'ölçüm' ? (
      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
        ölçüm
      </span>
    ) : kaynak === 'tahmin' ? (
      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
        tahmin
      </span>
    ) : (
      <span className="text-[9px] text-gray-400">—</span>
    );

  const th = 'text-left font-semibold text-gray-500 text-[10px] uppercase tracking-wide py-2 px-2';
  const td = 'py-2 px-2 text-xs text-gray-700 align-top';

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" lang="tr">
      <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          {Object.entries(SENARYOLAR).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setSenaryo(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                senaryo === k
                  ? 'text-white border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              style={senaryo === k ? { background: YESIL } : undefined}
            >
              {v.ad} · {fmtInt(v.aylik / 1000)}K ₺/ay
            </button>
          ))}
        </div>
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: KOYU }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* ── Başlık ─────────────────────────────────────────────────────── */}
        <div
          data-pdf-header
          className="border-b px-5 py-3 flex items-center justify-between"
          style={{ background: YESIL }}
        >
          <div>
            <p className="text-sm font-bold text-white tracking-tight">
              Europcar Türkiye — Programatik Ağırlıklı Medya Planı
            </p>
            <p className="text-[10px] text-white/80 mt-0.5">
              Eylül – Kasım 2026 · Placement hedeflemeli · {fmtCur(aylik)}/ay ·{' '}
              {fmtCur(donemToplam)} dönem toplamı
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold text-white/90">Glowytics</p>
            <p className="text-[9px] text-white/70">20 Ağustos 2026</p>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* ── 1. Kampanya detayı ─────────────────────────────────────── */}
          <div data-pdf-section className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Europcar Türkiye'nin <strong>programatik ağırlıklı</strong> sonbahar dönemi medya planı.
              İki hedef üzerine kurulu: <strong>ofis ve lokasyon trafiği</strong> ile{' '}
              <strong>marka bilinirliği</strong>. Satış hunisinin alt basamağı (marka araması, harita,
              rezervasyon) zaten çalışıyor; bu plan onun üstüne, <strong>talebin doğduğu ana</strong>{' '}
              çıkmak için kuruluyor: uçuş planlanırken, havalimanına inilirken, rota araştırılırken.
            </p>
            <p className="text-xs text-gray-600 mt-2 leading-relaxed">
              Planın ayırt edici tarafı <strong>envanterin isim isim seçilmesi</strong>. Açık pazarda
              &quot;otomatik dağıt&quot; yok; her yayıncı ayrı satır, ayrı raporlanır, kötü olan tek başına
              kapatılır. Hedefleme dört katmanda kurulur:{' '}
              <strong>yayıncı whitelist → sayfa düzeyi bağlam filtresi → coğrafi/POI katmanı → kara liste</strong>.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              {[
                ['Dönem', 'Eylül – Kasım 2026 · 3 ay'],
                ['Hedef', 'Lokasyon trafiği + bilinirlik'],
                ['Satın alma', 'DV360 — tek DSP, tek raporlama'],
                ['Ana KPI', 'Görünür CPM · erişim · lokasyon oturumu'],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-white border border-emerald-100 px-3 py-2">
                  <p className="text-[9px] font-semibold uppercase text-gray-400">{k}</p>
                  <p className="text-[11px] font-semibold text-gray-800 mt-0.5 leading-snug">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
              Karma satın alma kurgusu (dış DSP + yayıncı direkt) bilerek plana konmadı — tek eksen,
              tek raporlama. İkinci fazda açılabilir. Bu planın neden bu şekilde kurulduğu,{' '}
              <strong>&quot;Planın Dayanağı&quot;</strong> bölümünde hesabın kendi verisiyle anlatılıyor.
            </p>
          </div>

          {/* ── 2. Bütçe kırılımı ──────────────────────────────────────── */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">
              Bütçe Kırılımı — {SENARYOLAR[senaryo].ad} senaryo
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Aylık Net Medya" value={fmtCur(aylik)} sub="AHK ve stopaj hariç" />
              <Stat
                label={`Dönem Toplamı (${AY_SAYISI} ay)`}
                value={fmtCur(donemToplam)}
                sub="Eylül – Kasım 2026"
                tone="gray"
              />
              <Stat
                label="Programatik"
                value={fmtCur(programatikTutar)}
                sub={`Aylık bütçenin %${fmtDec(programatikPay * 100, 1)}'i`}
                tone="green"
              />
              <Stat
                label="Yerel & Harita"
                value={fmtCur(yerelTutar)}
                sub={`Hesabın en ucuz CPA'sı · %${fmtDec((yerelTutar / aylik) * 100, 1)}`}
                tone="indigo"
              />
              <Stat
                label="Ölçüm & Kreatif"
                value={fmtCur(olcumTutar)}
                sub="Doğrulama + format üretimi"
                tone="indigo"
              />
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
              Tutarlar <strong>net medya</strong>dır; ajans hizmet bedeli ve stopaj dahil değildir.
              Yukarıdaki üç düğmeden 250K / 400K / 500K ₺ senaryoları arasında geçiş yapılabilir —
              aşağıdaki bütün tablolar seçime göre yeniden hesaplanır.
            </p>
          </div>

          {/* ── 3. Kanal bazında plan ───────────────────────────────────────── */}
          <Section
            kicker="Medya Planı"
            title={`Kanal Bazında Bütçe ve Projeksiyon — ${fmtCur(aylik)}/ay`}
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={th}>#</th>
                    <th className={th}>Kalem</th>
                    <th className={`${th} text-right`}>₺/ay</th>
                    <th className={`${th} text-right`}>Pay</th>
                    <th className={`${th} text-right`}>Birim maliyet</th>
                    <th className={th}>Kaynak</th>
                    <th className={`${th} text-right`}>Tahmini hacim</th>
                  </tr>
                </thead>
                <tbody>
                  {satirlar.map((r) => (
                    <tr
                      key={r.kod}
                      className={`border-b border-gray-100 ${r.b === 0 ? 'opacity-40' : ''}`}
                    >
                      <td className={`${td} text-gray-400`}>{r.kod}</td>
                      <td className={td}>
                        <span className="font-semibold text-gray-800">{r.kalem}</span>
                        <span className="block text-[10px] text-gray-500 mt-0.5">{r.aciklama}</span>
                      </td>
                      <td className={`${td} text-right tabular-nums font-semibold`}>
                        {r.b === 0 ? '—' : fmtCur(r.b)}
                      </td>
                      <td className={`${td} text-right tabular-nums text-gray-500`}>
                        {r.b === 0 ? '—' : `%${fmtDec(r.pay * 100, 1)}`}
                      </td>
                      <td className={`${td} text-right tabular-nums`}>
                        {r.birim === 'cpm'
                          ? `CPM ${fmtDec(r.deger)} ₺`
                          : r.birim === 'cpc'
                          ? `CPC ${fmtDec(r.deger)} ₺`
                          : '—'}
                      </td>
                      <td className={td}>
                        <KaynakRozet kaynak={r.kaynak} />
                        <span className="block text-[9px] text-gray-400 mt-0.5 leading-tight">
                          {r.kaynakNot}
                        </span>
                      </td>
                      <td className={`${td} text-right tabular-nums`}>
                        {r.b === 0
                          ? '—'
                          : r.birim === 'cpm'
                          ? `${fmtInt(r.gosterim)} gösterim`
                          : r.birim === 'cpc'
                          ? `${fmtInt(r.tik)} tık`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td className={td}></td>
                    <td className={`${td} font-bold`}>Toplam</td>
                    <td className={`${td} text-right tabular-nums font-bold`}>{fmtCur(aylik)}</td>
                    <td className={`${td} text-right`}>%100</td>
                    <td className={td}></td>
                    <td className={td}></td>
                    <td className={`${td} text-right tabular-nums font-bold`}>
                      {fmtInt(toplamGosterim)} gösterim
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <Stat
                label="Programatik payı"
                value={`%${fmtDec(programatikPay * 100, 1)}`}
                sub={`${fmtCur(programatikTutar)}/ay`}
                tone="green"
              />
              <Stat
                label="Aylık gösterim"
                value={fmtInt(toplamGosterim)}
                sub="pDOOH dahil"
                tone="indigo"
              />
              <Stat
                label="Tahmini net erişim"
                value={fmtInt(erisim)}
                sub={`Ekran içi, frekans ${FREKANS} varsayımıyla`}
                tone="indigo"
              />
              <Stat
                label="Dönem toplamı"
                value={fmtCur(donemToplam)}
                sub={`${AY_SAYISI} ay · ${fmtInt(toplamGosterim * AY_SAYISI)} gösterim`}
                tone="gray"
              />
            </div>

            <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
              ⚠️ <strong>Erişim ve gösterim tahmindir, taahhüt değildir.</strong> CPM'lerin
              "tahmin" etiketli olanları teklif geldikçe değişecek ve tablo yeniden hesaplayacak.
              pDOOH gösterimi kişi bazlı erişime çevrilmedi — ekran gösterimi ile dijital gösterim
              aynı birim değildir, toplanırsa erişim şişer.
              {senaryo === 'A' && (
                <span>
                  {' '}
                  <strong>Odaklı senaryoda CTV ve pDOOH bilerek sıfır:</strong> 250K'da ikisine de
                  anlamlı frekans düşmez; parayı bölmek yerine ölçülebilir kalemlere yığmak daha
                  doğru.
                </span>
              )}
            </p>
          </Section>
          {/* ── 10. Takvim ─────────────────────────────────────────────── */}
          <Section kicker="Zamanlama" title="Neden Eylül–Kasım, ne zaman ne yapılacak" tone="gray">
            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              Search Console tıkları organik talebin mevsimini gösteriyor: zirve Haziran–Temmuz
              (68.930 tık), dip Ocak–Şubat. <strong>Eylül–Kasım düşen sezon</strong> — ve bilinirlik
              yatırımı için doğru pencere tam da budur. Talebin zirvesinde bilinirlik satın almak
              pahalıdır; hasat ilkbaharda alınır.
            </p>
            <div className="bg-white rounded-lg border border-gray-200 p-3 mb-3 overflow-x-auto">
              <svg width="760" height="130" viewBox="0 0 760 130" className="min-w-[600px]">
                {SEZON.map((s, i) => {
                  const bw = 56;
                  const gap = 12;
                  const x = i * (bw + gap) + 8;
                  const h = (s.tik / sezonMax) * 84;
                  const y = 96 - h;
                  const plan = ['2025-09', '2025-10', '2025-11'].includes(s.ay);
                  return (
                    <g key={s.ay}>
                      <rect
                        x={x}
                        y={y}
                        width={bw}
                        height={h}
                        rx="3"
                        fill={plan ? YESIL : '#d1d5db'}
                      />
                      <text
                        x={x + bw / 2}
                        y={y - 4}
                        textAnchor="middle"
                        fontSize="9"
                        fill="#6b7280"
                      >
                        {Math.round(s.tik / 1000)}K
                      </text>
                      <text
                        x={x + bw / 2}
                        y={112}
                        textAnchor="middle"
                        fontSize="9"
                        fill="#9ca3af"
                      >
                        {s.ay.slice(2)}
                      </text>
                    </g>
                  );
                })}
                <text x="8" y="126" fontSize="9" fill="#9ca3af">
                  Search Console organik tık · yeşil = geçen yılın aynı plan penceresi
                </text>
              </svg>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                {
                  ay: 'Eylül — Kur ve öğren',
                  m: [
                    'DV360 kurulumu, Floodlight etiketleri, deal ID\'lerin aktivasyonu',
                    'Kara liste ilk günden yüklü başlar',
                    'Whitelist geniş açılır, hafta hafta daraltılır',
                    'Görünürlük ve marka güvenliği doğrulaması açık',
                  ],
                },
                {
                  ay: 'Ekim — Daralt ve ölçekle',
                  m: [
                    'Kanıtlanan deal\'ler PMP\'den garantili (PG) yapıya taşınır',
                    'Zayıf placement\'lar kapatılır, bütçe güçlülere kayar',
                    'CTV ve pDOOH frekans dengesi ayarlanır',
                    'Lokasyon bazlı dinamik kreatif devreye girer',
                  ],
                },
                {
                  ay: 'Kasım — Sezon mesajı',
                  m: [
                    'Kış lastiği / 4×4 / kış kaçamağı mesajı',
                    'Hava durumu tetikleyicili kreatif',
                    'Kurumsal & filo dönem sonu iletişimi',
                    'Dönem raporu + ilkbahar planının veri temeli',
                  ],
                },
              ].map((f) => (
                <div key={f.ay} className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-xs font-bold text-gray-800 mb-2">{f.ay}</p>
                  <ul className="space-y-1">
                    {f.m.map((x) => (
                      <li key={x} className="text-[11px] text-gray-600 leading-relaxed flex gap-1.5">
                        <span style={{ color: YESIL }}>•</span>
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>
          {/* ── 6. Placement stratejisi ────────────────────────────────── */}
          <Section kicker="Yöntem" title="Placement hedeflemesi dört katmanda kurulur" tone="gray">
            <div className="grid md:grid-cols-2 gap-3">
              {[
                {
                  n: 1,
                  b: 'Yayıncı whitelist (PMP / PG)',
                  a: 'Envanteri isim isim biz belirliyoruz. Açık pazarda "otomatik dağıt" yok. Her deal ayrı satır (line item), ayrı raporlanır, kötü olan tek başına kapatılır.',
                },
                {
                  n: 2,
                  b: 'Bağlam (contextual) filtresi',
                  a: 'Aynı yayıncının içinde bile her sayfa alınmaz. Anahtar kelime + kategori filtresi: seyahat, rota, tatil, havalimanı, otomobil VAR; asayiş, magazin, ölüm haberi YOK.',
                },
                {
                  n: 3,
                  b: 'Coğrafi & POI katmanı',
                  a: 'Ofis çevresi yarıçapı, havalimanı terminalleri, tren garları, otoyol çıkışları. Kreatif de coğrafyayla değişir: şehrin adı ve ofisin kendisi görselde geçer.',
                },
                {
                  n: 4,
                  b: 'Kara liste & marka güvenliği',
                  a: 'Ön-teklif (pre-bid) filtresi + hesap düzeyinde kalıcı hariç tutma listesi. Aşağıdaki liste ilk günden yüklenir, haftalık büyütülür.',
                },
              ].map((k) => (
                <div key={k.n} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-start gap-2">
                    <span
                      className="text-[10px] font-bold text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0"
                      style={{ background: YESIL }}
                    >
                      {k.n}
                    </span>
                    <div>
                      <p className="text-xs font-bold text-gray-800">{k.b}</p>
                      <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{k.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
          {/* ── 7. WHITELIST ───────────────────────────────────────────── */}
          <Section kicker="Nereye çıkıyoruz" title="Whitelist — envanter listesi">
            <p className="text-xs text-gray-600 mb-3 leading-relaxed">
              Yeşil etiketli satırlar tahmin değil: o siteler <strong>şu anda bile</strong> Europcar'a
              trafik gönderiyor ya da kendi reklam verimizde ölçüldü. Sarı olanlar için teklif
              alınacak, griler için önce programatik erişilebilirliğinin doğrulanması gerekiyor.
            </p>
            <div className="space-y-3">
              {WHITELIST.map((g) => (
                <div key={g.grup} className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <p className="text-xs font-bold text-gray-800">{g.grup}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{g.gerekce}</p>
                  </div>
                  <table className="w-full border-collapse">
                    <tbody>
                      {g.envanter.map((e) => (
                        <tr key={e.ad} className="border-b border-gray-100 last:border-0">
                          <td className={`${td} font-medium w-2/5`}>{e.ad}</td>
                          <td className={`${td} text-gray-500`}>{e.kanit}</td>
                          <td className={`${td} text-right w-24`}>
                            <DurumRozet durum={e.durum} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </Section>
          {/* ── 8. KARA LİSTE ──────────────────────────────────────────── */}
          <Section kicker="Nereye çıkmıyoruz" title="Kara liste — hepsi kendi verimizden çıkarıldı" tone="gray">
            <div className="space-y-2">
              {KARALISTE.map((k) => (
                <div key={k.baslik} className="rounded-lg border border-red-200 bg-white p-3">
                  <p className="text-xs font-bold text-red-800">{k.baslik}</p>
                  <p className="text-[11px] text-gray-500 mt-1 font-mono">{k.kanit}</p>
                  <p className="text-[11px] text-gray-700 mt-1.5 leading-relaxed">{k.neden}</p>
                </div>
              ))}
            </div>
          </Section>
          {/* ── 9. Coğrafya ────────────────────────────────────────────── */}
          <Section kicker="Coğrafya" title="Bütçe hangi şehre, hangi ülkeye">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-gray-800 mb-2">
                  Türkiye — GA4 oturumu (son 90 gün)
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className={th}>Şehir</th>
                      <th className={`${th} text-right`}>Oturum</th>
                      <th className={th}>Ağırlık</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SEHIRLER.map((s) => (
                      <tr key={s.sehir} className="border-b border-gray-100">
                        <td className={`${td} font-medium`}>
                          {s.sehir}
                          <span className="block text-[10px] text-gray-400 font-normal">
                            {s.not}
                          </span>
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{fmtInt(s.oturum)}</td>
                        <td className={td}>
                          <span
                            className={`text-[10px] font-semibold ${
                              s.agirlik === 'Yüksek' ? 'text-emerald-700' : 'text-gray-500'
                            }`}
                          >
                            {s.agirlik}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-800 mb-2">
                  Yurt dışı — GA4 oturumu (son 180 gün)
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className={th}>Ülke</th>
                      <th className={`${th} text-right`}>Oturum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {YURTDISI.map((y) => (
                      <tr key={y.ulke} className="border-b border-gray-100">
                        <td className={`${td} font-medium`}>
                          {y.ulke}
                          {y.not && (
                            <span className="block text-[10px] text-gray-400 font-normal">
                              {y.not}
                            </span>
                          )}
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{fmtInt(y.oturum)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[11px] text-amber-900 leading-relaxed">
                    ⚠️ <strong>Singapur 18.059 oturumla üçüncü sırada görünüyor — hedeflemeye
                    alınmadı.</strong> Bu hacim büyük olasılıkla veri merkezi/bot trafiği. Gerçek
                    bir pazar sinyali sayılıp bütçe ayrılmamalı; önce filtrelenmeli.
                  </p>
                </div>
                <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[11px] text-emerald-900 leading-relaxed">
                    🔑 <strong>Almanya tek başına 30.549 oturum.</strong> Hollanda, Fransa, İsviçre,
                    Avusturya ve Belçika ile birlikte Avrupa kaynaklı hacim{' '}
                    {fmtInt(YURTDISI.reduce((s, y) => s + y.oturum, 0))} oturum. Türkiye'ye uçan
                    yolcuya, <strong>kalkış ülkesinde</strong> ulaşmak için ayrı bir kurgu hak
                    ediyor — havalimanı ve havayolu envanteri burada da geçerli.
                  </p>
                </div>
              </div>
            </div>
          </Section>
          {/* ── 11. Kreatif ────────────────────────────────────────────── */}
          <Section kicker="Kreatif" title="Format listesi ve neden bu ağırlık">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Ölçüm net: organik gösterimlerin <strong>%80,3'ü mobil</strong> (4.165.928 /
                  5.188.468), GA4 oturumlarının <strong>%73,2'si mobil</strong> (250.155 / 341.682).
                  Kreatif üretimi buna göre önceliklenmeli — masaüstü boyutları ikincil.
                </p>
                <p className="text-xs text-gray-700 leading-relaxed mt-2">
                  Ancak masaüstünün <strong>etkileşim oranı %77</strong>, mobilin %51. Yani mobil
                  erişim, masaüstü dönüşüm getiriyor. Retargeting kreatifleri masaüstünde de tam
                  kadro olmalı.
                </p>
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[11px] text-amber-900 leading-relaxed">
                    🔴 <strong>Türkçe tipografi tuzağı:</strong> CSS <code>text-transform:
                    uppercase</code> Türkçe "İ" harfini bozar. Tüm kreatif şablonlarında sarmalayıcıya{' '}
                    <code>lang="tr"</code> konulmalı; DOOH ve CTV çıktılarında büyük harf kullanımı
                    ayrıca kontrol edilmeli.
                  </p>
                </div>
              </div>
              <div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className={th}>Kanal</th>
                      <th className={th}>Format</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Display (mobil öncelik)', '320×100, 300×250, 300×600, 336×280'],
                      ['Display (masaüstü)', '728×90, 970×250, 160×600'],
                      ['Sosyal / dikey', '1080×1080, 1080×1920'],
                      ['Online video', '6 sn bumper · 15 sn · 30 sn'],
                      ['CTV / BVOD', '15 sn ve 20 sn, ses açık kurgu'],
                      ['pDOOH', '10 sn döngü, SESSİZ, tek mesaj, uzaktan okunur punto'],
                      ['Dinamik retargeting', 'Şehir + ofis + araç sınıfı değişkenli şablon'],
                    ].map(([k, f]) => (
                      <tr key={k} className="border-b border-gray-100">
                        <td className={`${td} font-medium`}>{k}</td>
                        <td className={`${td} text-gray-600`}>{f}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          {/* ── 2. Mevcut durum röntgeni ───────────────────────────────── */}
          <Section
            kicker="Planın Dayanağı"
            title="Para nereye gitti — kendi hesabımızın gerçek placement raporu"
            tone="gray"
          >
            <p className="text-xs text-gray-700 leading-relaxed">
              Bu plan neden envanteri tek tek seçmek üzerine kurulu? Çünkü hesabın kendi geçmişi bunun
              tersini deneyip sonucunu gösteriyor. Europcar'ın <strong>son 180 günde hiç display ve hiç
              video kampanyası yok</strong> — yalnızca 3 Performance Max ve 15 Arama kampanyası dönüyor.
              Programatik sıfırdan kurulacak; bu bir eksiklik değil, avantaj: kurulacak yapı hiçbir kötü
              alışkanlığı devralmak zorunda değil.
            </p>
            <p className="text-xs text-gray-700 leading-relaxed mt-2">
              Ölçülebilen son display penceresinde ({GDN.baslangic} – {GDN.bitis}) bütçe{' '}
              <strong>{fmtInt(GDN.placementSayisi)} farklı placement'a</strong> dağılmış ve iki
              remarketing kampanyası {fmtInt(GDN_KAMPANYA.gosterim)} gösterim ile{' '}
              {fmtCur(GDN_KAMPANYA.maliyet)} karşılığında <strong>sıfır dönüşüm</strong> üretmiş.
              Aynı dönemde YouTube kampanyası {fmtInt(YT_KAMPANYA.gosterim)} gösterimde{' '}
              {YT_KAMPANYA.tik} tık almış. Sorun bütçenin küçüklüğü değil, <strong>seçim
              yapılmamış olması</strong>.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 mb-4">
              <Stat
                label="Dağıldığı placement"
                value={fmtInt(GDN.placementSayisi)}
                sub="Tek bir yayıncı ilişkisi kurulmadan"
                tone="red"
              />
              <Stat
                label="Derin makale URL'sine giden"
                value={`%${fmtDec((DERIN.maliyet / GDN.maliyet) * 100, 1)}`}
                sub={`${fmtCur(DERIN.maliyet)} · ${fmtInt(DERIN.adet)} ayrı adres`}
                tone="red"
              />
              <Stat label="GDN dönüşümü" value="0" sub="Kampanya seviyesinden doğrulandı" tone="red" />
              <Stat
                label="YouTube kampanyası"
                value={`${YT_KAMPANYA.tik} tık`}
                sub={`${fmtInt(YT_KAMPANYA.gosterim)} gösterim karşılığında`}
                tone="red"
              />
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              Aşağıdaki hiçbir rakam sektör ortalaması değil; Europcar'ın Google Ads hesabından,{' '}
              {GDN.baslangic} – {GDN.bitis} arası {fmtInt(GDN.gosterim)} gösterim ve{' '}
              {fmtCur(GDN.maliyet)} harcama üzerinden.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-white">
                    <th className={th}>Alan</th>
                    <th className={`${th} text-right`}>Gösterim</th>
                    <th className={`${th} text-right`}>Tık</th>
                    <th className={`${th} text-right`}>CTR</th>
                    <th className={`${th} text-right`}>CPM</th>
                    <th className={`${th} text-right`}>Harcama</th>
                    <th className={th}>Karar</th>
                  </tr>
                </thead>
                <tbody>
                  {ALANLAR.map((a) => {
                    const iyi = a.ctr >= 1.5;
                    const kotu = a.ctr < 0.6;
                    return (
                      <tr key={a.alan} className="border-b border-gray-100 bg-white">
                        <td className={`${td} font-medium`}>{a.alan}</td>
                        <td className={`${td} text-right tabular-nums`}>{fmtInt(a.gosterim)}</td>
                        <td className={`${td} text-right tabular-nums`}>{fmtInt(a.tik)}</td>
                        <td
                          className={`${td} text-right tabular-nums font-semibold ${
                            iyi ? 'text-emerald-700' : kotu ? 'text-red-600' : ''
                          }`}
                        >
                          %{fmtDec(a.ctr)}
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{fmtDec(a.cpm)} ₺</td>
                        <td className={`${td} text-right tabular-nums`}>{fmtCur(a.maliyet)}</td>
                        <td className={td}>
                          {iyi ? (
                            <span className="text-[10px] font-semibold text-emerald-700">
                              whitelist
                            </span>
                          ) : kotu ? (
                            <span className="text-[10px] font-semibold text-red-600">kara liste</span>
                          ) : (
                            <span className="text-[10px] text-gray-500">bağlam filtresiyle</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[11px] font-bold text-red-800">
                  Derin URL vs. alan adı — asıl bulgu
                </p>
                <p className="text-[11px] text-red-900 mt-1 leading-relaxed">
                  Yalnız <strong>{ANASAYFA.adet} placement</strong> alan/ana sayfa düzeyinde
                  ({fmtCur(ANASAYFA.maliyet)}). Buna karşılık{' '}
                  <strong>{fmtInt(DERIN.adet)} derin makale adresi</strong> {fmtCur(DERIN.maliyet)}{' '}
                  yemiş. Yani bütçe yayıncıya değil, o gün ne olduysa o habere gitmiş.
                </p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[11px] font-bold text-red-800">Tek uygulama, bütçenin %14'ü</p>
                <p className="text-[11px] text-red-900 mt-1 leading-relaxed">
                  Sözcü iOS uygulaması tek başına {fmtCur(SOZCU_IOS.maliyet)} —{' '}
                  {fmtInt(SOZCU_IOS.gosterim)} gösterim, CPM {fmtDec(SOZCU_IOS.cpm)} ₺. Placement
                  başına harcama tavanı olmadığı için bir envanter kampanyayı domine etmiş.
                </p>
              </div>
            </div>
          </Section>
          {/* ── 3. Meta placement ──────────────────────────────────────── */}
          <Section kicker="Planın Dayanağı" title="Meta tarafında da aynı sorun var — Audience Network">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={th}>Yerleşim</th>
                    <th className={`${th} text-right`}>Gösterim</th>
                    <th className={`${th} text-right`}>Tık</th>
                    <th className={`${th} text-right`}>CTR</th>
                    <th className={`${th} text-right`}>CPM</th>
                    <th className={`${th} text-right`}>Harcama</th>
                    <th className={th}>Karar</th>
                  </tr>
                </thead>
                <tbody>
                  {META.map((m) => (
                    <tr key={m.yer} className="border-b border-gray-100">
                      <td className={`${td} font-medium`}>{m.yer}</td>
                      <td className={`${td} text-right tabular-nums`}>{fmtInt(m.gosterim)}</td>
                      <td className={`${td} text-right tabular-nums`}>{fmtInt(m.tik)}</td>
                      <td
                        className={`${td} text-right tabular-nums font-semibold ${
                          m.karar === 'kapat' ? 'text-red-600' : ''
                        }`}
                      >
                        %{fmtDec(m.ctr)}
                      </td>
                      <td className={`${td} text-right tabular-nums`}>{fmtDec(m.cpm)} ₺</td>
                      <td className={`${td} text-right tabular-nums`}>{fmtCur(m.harcama)}</td>
                      <td className={td}>
                        <span
                          className={`text-[10px] font-semibold ${
                            m.karar === 'kapat'
                              ? 'text-red-600'
                              : m.karar === 'kalsın'
                              ? 'text-emerald-700'
                              : 'text-amber-700'
                          }`}
                        >
                          {m.karar}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-600 mt-3 leading-relaxed">
              <strong>%16,25 CTR bir başarı değil.</strong> Audience Network, uygulama içi tam ekran
              ve banner envanteri — o CTR kaza dokunuşunun imzasıdır. GA4 tarafı da bunu
              doğruluyor: <code className="bg-gray-100 px-1 rounded">an</code> kaynağından{' '}
              <strong>24.717 oturum</strong> gelmiş; bu, Facebook ve Instagram feed
              yerleşimlerinin toplamından (≈10.500) fazla. Placement disiplini yalnızca programatikte
              değil, Meta'da da kurulmalı.
            </p>
          </Section>
          {/* ── 4. Aşılması gereken eşik ───────────────────────────────── */}
          <Section
            kicker="Planın Dayanağı"
            title="Programatiğin yanında duracağı gerçek maliyetler"
            tone="gray"
          >
            <p className="text-xs text-gray-600 mb-3 leading-relaxed">
              Son 90 gün, Google Ads. Bilinirlik kalemleri CPA ile ölçülmez — ama bütçe savunulurken
              bu tablo lazım: hesabın hangi kalemi ucuz, hangisi pahalı.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-white">
                    <th className={th}>Kampanya</th>
                    <th className={th}>Tip</th>
                    <th className={`${th} text-right`}>Harcama</th>
                    <th className={`${th} text-right`}>Dönüşüm</th>
                    <th className={`${th} text-right`}>CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {GADS.map((g) => (
                    <tr key={g.kampanya} className="border-b border-gray-100 bg-white">
                      <td className={`${td} font-medium`}>{g.kampanya}</td>
                      <td className={td}>
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200">
                          {g.tip}
                        </span>
                      </td>
                      <td className={`${td} text-right tabular-nums`}>{fmtCur(g.maliyet)}</td>
                      <td className={`${td} text-right tabular-nums`}>{fmtInt(g.donusum)}</td>
                      <td
                        className={`${td} text-right tabular-nums font-semibold ${
                          g.cpa <= 100 ? 'text-emerald-700' : g.cpa >= 1000 ? 'text-red-600' : ''
                        }`}
                      >
                        {fmtCur(g.cpa)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[11px] text-emerald-900 leading-relaxed">
                🔑 <strong>Harita kampanyaları hesabın en verimli kalemi:</strong> Kocaeli 259
                dönüşüm / CPA 9 ₺, Edremit 261 dönüşüm / CPA 27 ₺. Aynı şehirlerin arama
                kampanyaları 443–1.802 ₺ CPA'da. Bu yüzden plan yerel/harita katmanına dokunmuyor,
                aksine bütçe ayırıyor — programatik onun yerine değil, üstüne geliyor.
              </p>
            </div>
          </Section>
          {/* ── 12. Ölçüm ön koşulu ────────────────────────────────────── */}
          <Section kicker="Ön Koşul" title="Ölçüm düzelmeden bu bütçe harcanmamalı" tone="gray">
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
              <p className="text-xs font-bold text-red-900">
                Bu plandaki en büyük risk envanter seçimi değil, ölçümün kendisi.
              </p>
              <ul className="mt-2 space-y-1.5">
                {[
                  'GA4 tarafında 8 Aralık 2025\'te tag kırılması yaşandı: view_item ve add_to_cart o tarihten sonra hiç tetiklenmedi, purchase 31 gün boyunca hiç gelmedi.',
                  'Tek sayfa uygulaması (SPA) geçişlerinde GA4\'e hiç istek gitmiyor — sayfa değişimleri ölçülmüyor.',
                  'Sitede hayalet bir G-XXXXXXX ölçüm kimliği canlı duruyor.',
                  'Consent Mode kurulu değil; Avrupa kaynaklı trafikte bu doğrudan veri kaybı demek.',
                  'GA4 veri saklama 2 ay ve BigQuery aktarımı yok — kampanya bittikten sonra geriye dönüp bakılamaz.',
                ].map((x) => (
                  <li key={x} className="text-[11px] text-red-900 leading-relaxed flex gap-1.5">
                    <span className="font-bold">·</span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-red-900 mt-2 leading-relaxed font-semibold">
                Programatik bütçe bu haliyle açılırsa sonuç "işe yaramadı" diye görünür — oysa
                ölçülmemiş olur. dataLayer düzeltmesi ve Floodlight kurulumu, medya başlangıcının
                ön koşuludur.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-xs font-bold text-gray-800">Nasıl ölçeceğiz</p>
                <ul className="mt-1.5 space-y-1">
                  {[
                    'Görünür CPM (vCPM) ve görünürlük oranı — hedef ≥ %70',
                    'Video tamamlanma oranı (VTR) ve CTV\'de tam izlenme',
                    'Lokasyon sayfası oturumu ve rezervasyon başlangıcı',
                    'Marka arama hacmi — Search Console ile kampanya öncesi/sonrası',
                    'Haftalık placement raporu → whitelist/kara liste güncellemesi',
                  ].map((x) => (
                    <li key={x} className="text-[11px] text-gray-600 leading-relaxed flex gap-1.5">
                      <span style={{ color: YESIL }}>•</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-xs font-bold text-gray-800">Kurulum disiplini</p>
                <ul className="mt-1.5 space-y-1">
                  {[
                    'Her deal ayrı line item — kötü olan tek başına kapatılabilsin',
                    'Placement başına harcama tavanı (Sözcü uygulaması vakası tekrarlanmasın)',
                    'Sıklık sınırı: günde 3, haftada 12',
                    'Ön-teklif marka güvenliği filtresi + kalıcı hariç tutma listesi',
                    'Açık pazar (open exchange) yalnızca ölçülü test bütçesiyle, varsayılan kapalı',
                  ].map((x) => (
                    <li key={x} className="text-[11px] text-gray-600 leading-relaxed flex gap-1.5">
                      <span style={{ color: YESIL }}>•</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>
          {/* ── 13. Varsayımlar ────────────────────────────────────────── */}
          <Section kicker="Şeffaflık" title="Neyi ölçtük, neyi varsaydık">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs font-bold text-emerald-800 mb-1.5">Ölçüm (ambar / canlı API)</p>
                <ul className="space-y-1">
                  {[
                    'Tüm GDN placement, CPM ve CTR rakamları — 1 Eki–5 Kas 2025',
                    'GDN ve YouTube kampanyalarının 0 dönüşümü — kampanya seviyesinden doğrulandı',
                    'Meta yerleşim performansı — son 90 gün',
                    'Google Ads CPA\'ları — son 90 gün',
                    'GA4 şehir, ülke, cihaz ve referans verisi',
                    'Search Console mevsimsellik ve cihaz kırılımı',
                  ].map((x) => (
                    <li key={x} className="text-[11px] text-emerald-900 leading-relaxed flex gap-1.5">
                      <span>✓</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-bold text-amber-800 mb-1.5">Varsayım (onaya açık)</p>
                <ul className="space-y-1">
                  {[
                    'Dönem: Eylül–Kasım 2026 — tarih iletilmedi, mevsimsellikten türetildi',
                    'CTV, pDOOH, seyahat PMP ve YouTube whitelist CPM\'leri — teklif alınmadı',
                    'Frekans 4 varsayımı ve buradan türeyen erişim rakamı',
                    'Türk yayıncıların PMP/deal desteği envanter sahibiyle teyit edilmedi',
                    'Yerli BVOD platformlarının programatik erişilebilirliği doğrulanmadı',
                    'Ajans hizmet bedeli ve stopaj bu tabloya DAHİL DEĞİL — net medya tutarıdır',
                  ].map((x) => (
                    <li key={x} className="text-[11px] text-amber-900 leading-relaxed flex gap-1.5">
                      <span>⚠</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
              Veri kaynağı: Glowytics ambarı — Google Ads placement/kampanya/coğrafya tabloları,
              Meta yerleşim tablosu, GA4 coğrafya/cihaz/kaynak tabloları, Search Console günlük
              tabloları. Rapor 20 Ağustos 2026 tarihinde üretildi.
            </p>
          </Section>

        </div>
      </div>
    </div>
  );
}
